import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PropertyGeospatialNoiseDto } from '@workspace/data-types';
import { NoiseSourceDisplayPipe } from './noise-source-display.pipe';

@Component({
  selector: 'etxe-property-noise-card',
  standalone: true,
  imports: [CommonModule, MatExpansionModule, MatIconModule, MatTooltipModule, NoiseSourceDisplayPipe],
  templateUrl: './noise-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyNoiseCardComponent {
  noise = input<PropertyGeospatialNoiseDto | null | undefined>();
  expanded = model<boolean>(false);

  categoryInfo = computed(() => {
    const cat = this.noise()?.category;
    switch (cat) {
      case 'calm':
        return {
          label: 'Environnement calme',
          classes: 'bg-jade-a2 text-jade-11 border-jade-a4',
          dotBg: 'bg-jade-9',
          icon: 'volume_down',
          colorName: 'jade',
          description: 'Niveau sonore bas, propice au repos et à la tranquillité résidentielle.',
        };
      case 'moderate':
        return {
          label: 'Environnement modéré',
          classes: 'bg-amber-a2 text-amber-11 border-amber-a4',
          dotBg: 'bg-amber-9',
          icon: 'volume_up',
          colorName: 'amber',
          description: 'Bruit ambiant courant correspondant au cadre de vie urbain moyen.',
        };
      case 'exposed':
        return {
          label: 'Environnement exposé',
          classes: 'bg-ruby-a2 text-ruby-11 border-ruby-a4',
          dotBg: 'bg-ruby-9',
          icon: 'warning',
          colorName: 'ruby',
          description:
            'Exposition sensible aux nuisances sonores d’infrastructures de transport ou urbaines.',
        };
      default:
        return null;
    }
  });

  soundEquivalence = computed(() => {
    const db = this.noise()?.levelDb;
    if (db === undefined || db === null) return 'Niveau non mesuré';
    if (db < 40) return 'Comparable à un chuchotement ou une campagne paisible';
    if (db < 50) return 'Comparable au calme d’une bibliothèque ou d’un quartier résidentiel';
    if (db < 60) return 'Comparable à une conversation à voix normale en intérieur';
    if (db < 70) return 'Comparable à un bureau animé ou une circulation modérée';
    return 'Comparable à une artère passante ou proximité d’un axe de transport';
  });

  gaugePercent = computed(() => {
    const db = this.noise()?.levelDb;
    if (db === undefined || db === null) return 0;
    // Scale between 30 dB (very quiet) and 85 dB (very noisy)
    const min = 30;
    const max = 85;
    const clamped = Math.min(Math.max(db, min), max);
    return Math.round(((clamped - min) / (max - min)) * 100);
  });

  getSourceIcon(source: string): string {
    const s = source.toLowerCase();
    if (s.includes('bus') || s.includes('transit')) return 'directions_bus';
    if (s.includes('commercial') || s.includes('commerce') || s.includes('shop')) return 'storefront';
    if (s.includes('industrie') || s.includes('usine') || s.includes('industrial') || s.includes('icpe'))
      return 'factory';
    if (s.includes('fer') || s.includes('train') || s.includes('sncf') || s.includes('rail')) return 'train';
    if (
      s.includes('route') ||
      s.includes('routier') ||
      s.includes('voiture') ||
      s.includes('road') ||
      s.includes('highway')
    )
      return 'directions_car';
    if (s.includes('avion') || s.includes('aérien') || s.includes('aéroport') || s.includes('air'))
      return 'flight';
    return 'sensors';
  }
}
